import React, { useState } from 'react';
import axios from "axios";
import { connect } from "react-redux";
import { setUser, refreshToken } from "../../../store/actions/authActions";
import { getUserDetailsFromCookie } from "../../../utils/getCookies";
import secureApiService from '../../../services/secureApi';
import loadingGif from "../../../assets/loading-button.gif";
import payCred from "../../../assets/img-payCred.png";
import arrowBack from "../../../assets/arrow-back-outline-1-1.svg";
import Message from './Message';
import TextMaskCardCPF from "../../../utils/TextMaskCardCPF";
import TextMaskCEP from "../../../utils/TextMaskCEP";
import cpfValidator from "../../../utils/validar-cpf-cnpj";
import pplansData from "../../../utils/pplansData";
import { states } from "../../../utils/states";

/**
 * PixPaymentButton - Componente para pagamento via PIX (V2)
 *
 * COMPORTAMENTO V2:
 * - PIX agora é EXPLÍCITO (não mais fallback automático)
 * - Usuário escolhe PIX voluntariamente
 * - Gera QR Code e código copia-e-cola
 * - Coleta dados necessários: Nome, CPF, Telefone, Endereço
 *
 * @version 2.0.0
 */
const PixPaymentButton = ({ selectedpaymentplan: pplan, form, goToStep }) => {
  const user = getUserDetailsFromCookie();
  const pplanData = pplansData[pplan];

  const [loading, setLoading] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [qrCodeLoading, setQrCodeLoading] = useState(true);
  const [qrCodeError, setQrCodeError] = useState(false);
  const [currentQrUrl, setCurrentQrUrl] = useState(null);

  // Form fields
  const [name, setName] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [cpfNumNotValid, setCpfNumNotValid] = useState(false);

  // Address fields
  const [street, setStreet] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [cepNotValid, setCepNotValid] = useState(false);

  /**
   * Define a URL do QR Code quando pixData muda
   * Tag <img> lida naturalmente com redirects 302, sem problemas de CORS
   */
  React.useEffect(() => {
    if (pixData?.qr_code_url) {
      // Tenta primeiro com .svg (formato mais comum do Yapay)
      setCurrentQrUrl(pixData.qr_code_url + '.svg');
      setQrCodeLoading(true);
      setQrCodeError(false);
    }
  }, [pixData]);

  // Formatter para exibir valores em BRL
  const formatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  // Calcula o preço total com desconto (PIX é sempre à vista)
  const calculateTotalPrice = () => {
    let totalPrice = parseFloat(pplanData.totalAnnualPrice.replace(",", "."));
    if (form.discountPercentage) {
      const priceDiscount = totalPrice - (totalPrice * form.discountPercentage) / 100;
      return priceDiscount;
    }
    return totalPrice;
  };

  const toCent = (amount) => {
    if (typeof amount === 'number') {
      return Math.round(amount * 100);
    }
    const str = amount.toString().replace(",", ".");
    return str.length < 3 || str[str.length - 3] === "."
      ? Number(str.replace(".", ""))
      : Number(str.replace(".", "")) * 100;
  };

  const fillAddress = (data) => {
    setStreet(data.logradouro || '');
    setState(data.uf || '');
    setCity(data.localidade || '');
    setNeighborhood(data.bairro || '');
  };

  const handleCepChange = async (value) => {
    setPostalCode(value);

    const cleanCep = value.replace(/\D/g, '');
    setCepNotValid(cleanCep.length > 0 && cleanCep.length !== 8);

    if (cleanCep.length === 8) {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GOLANG_API}/cep/${cleanCep}`);
        if (response.data && !response.data.erro) {
          fillAddress(response.data);
        }
      } catch (err) {
        console.error('Erro ao buscar CEP:', err);
      }
    }
  };

  const validateForm = () => {
    const errors = [];

    if (!name || name.trim() === '') {
      errors.push('Nome completo é obrigatório');
    }

    if (!documentNumber || documentNumber.trim() === '') {
      errors.push('CPF/CNPJ é obrigatório');
    } else {
      const cleanDoc = documentNumber.replace(/\D/g, '');
      const isValid = cpfValidator.validCpf(cleanDoc) || cpfValidator.validCnpj(cleanDoc);
      if (!isValid) {
        errors.push('CPF/CNPJ inválido');
        setCpfNumNotValid(true);
      } else {
        setCpfNumNotValid(false);
      }
    }

    if (!phone || phone.trim() === '') {
      errors.push('Telefone é obrigatório');
    }

    // Validação de endereço
    if (!postalCode || postalCode.trim() === '') {
      errors.push('CEP é obrigatório');
    } else {
      const cleanCep = postalCode.replace(/\D/g, '');
      if (cleanCep.length !== 8) {
        errors.push('CEP inválido');
        setCepNotValid(true);
      } else {
        setCepNotValid(false);
      }
    }

    if (!street || street.trim() === '') {
      errors.push('Logradouro é obrigatório');
    }

    if (!streetNumber || streetNumber.trim() === '') {
      errors.push('Número é obrigatório');
    }

    if (!city || city.trim() === '') {
      errors.push('Cidade é obrigatória');
    }

    if (!state || state.trim() === '') {
      errors.push('Estado é obrigatório');
    }

    return errors;
  };

  const handlePixPayment = async (e) => {
    e.preventDefault();

    setError(null);
    setStatus(null);

    // Validar formulário
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      setStatus('error');
      return;
    }

    setLoading(true);

    const authURL = `${process.env.REACT_APP_GOLANG_API}/auth/signup`;

    // Autenticar usuário
    let token;
    try {
      const authRes = await axios.post(authURL, {
        email: user && user.email ? user.email : form.email,
      });
      setUser(authRes.data.user, false);
      token = authRes.data.token;
    } catch (err) {
      console.error("Erro na autenticação:", err);
      setError("Erro ao autenticar. Tente novamente.");
      setStatus("error");
      setLoading(false);
      return;
    }

    // V2: Payload para PIX
    let body = {
      customer: {
        address: {
          line1: street.trim(),
          number: streetNumber.trim(),
          postalCode: postalCode.replace(/\D/g, '').trim(),
          state: state.trim(),
          city: city.trim(),
          neighborhood: neighborhood.trim(),
        },
        email: form.email,
        name: name.trim(),
        documentNumber: documentNumber.replace(/\D/g, '').trim(),
        phones: {
          mobile_phone: phone.replace(/\D/g, '').trim(),
        },
      },
      pplan: pplan,
      paymentMethod: 'pix',  // ← EXPLÍCITO (V2)
      installments: 1,       // PIX sempre à vista
      planPrice: toCent(calculateTotalPrice()),
    };

    // Adiciona cupom se existir
    if (form.coupon) {
      body.coupon = form.coupon.id;
      body.discount = form.discountPercentage || 0;
    }

    try {
      console.log('[V2 PIX] Calling /api/v2/subscriptions with paymentMethod: pix');
      const response = await secureApiService.customRequest(
        'POST',
        '/subscriptions',
        body,
        { Authorization: `Bearer ${token}` }
      );

      // Atualizar token de sessão
      await refreshToken(token);

      // Verifica se retornou dados do PIX
      if (response && response.pix) {
        setPixData(response.pix);
        setStatus("success");
      } else {
        setError('Erro ao gerar código PIX. Dados não retornados pelo servidor.');
        setStatus("error");
      }
    } catch (err) {
      console.error('[V2 PIX] Error:', err);

      const responseData = err.response?.data || {};
      const { data, details, message } = responseData;

      let errorMessage = 'Erro ao processar pagamento PIX';

      if (message) {
        errorMessage = message;
      } else if (details) {
        errorMessage = typeof details === 'string' ? details : JSON.stringify(details);
      } else if (data) {
        errorMessage = data;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      // Método 1: Tenta com Clipboard API (moderno, requer HTTPS)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setStatus("success");
        setError("✅ Código PIX copiado com sucesso!");
        setTimeout(() => {
          setError(null);
          setStatus(null);
        }, 3000);
        return;
      }

      // Método 2: Fallback com execCommand (antigo, mas funciona em HTTP)
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        setStatus("success");
        setError("✅ Código PIX copiado com sucesso!");
        setTimeout(() => {
          setError(null);
          setStatus(null);
        }, 3000);
      } else {
        throw new Error('execCommand falhou');
      }
    } catch (err) {
      console.error('[PIX] Erro ao copiar:', err);
      setStatus("error");
      setError("❌ Não foi possível copiar automaticamente. Selecione o texto acima e copie manualmente (Ctrl+C).");
      setTimeout(() => {
        setError(null);
        setStatus(null);
      }, 5000);
    }
  };

  /**
   * Handler quando a imagem carrega com sucesso
   */
  const handleQrCodeLoad = () => {
    setQrCodeLoading(false);
    setQrCodeError(false);
    console.log('[QR Code] Carregado com sucesso:', currentQrUrl);
  };

  /**
   * Handler quando a imagem falha ao carregar
   * Tenta formatos alternativos antes de desistir
   */
  const handleQrCodeError = () => {
    const currentUrl = currentQrUrl;

    // Se falhou com .svg, tenta .png
    if (currentUrl && currentUrl.endsWith('.svg')) {
      console.warn('[QR Code] Falha ao carregar .svg, tentando .png');
      setCurrentQrUrl(pixData.qr_code_url + '.png');
      return;
    }

    // Se falhou com .png, tenta sem extensão
    if (currentUrl && currentUrl.endsWith('.png')) {
      console.warn('[QR Code] Falha ao carregar .png, tentando URL base');
      setCurrentQrUrl(pixData.qr_code_url);
      return;
    }

    // Se todas falharam, mostra erro
    console.error('[QR Code] Falha ao carregar de todas as URLs tentadas');
    setQrCodeLoading(false);
    setQrCodeError(true);
  };

  // Se PIX foi gerado, mostrar QR Code
  if (pixData) {
    return (
      <div className="data-creditCard">
        <img
          className="btn-prev"
          src={arrowBack}
          alt="arrow back"
          onClick={() => goToStep(1)}
          style={{ outline: "none" }}
        />

        <div className="pix-qrcode-container" style={{
          textAlign: 'center',
          padding: '1rem',
          paddingBottom: '3rem',
          background: '#f9f9f9',
          borderRadius: '8px',
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch', // Smooth scroll no iOS
          scrollBehavior: 'smooth',
        }}>
          <div className="flex-center margin-bottom margin-medium">
            <img
              src={payCred}
              loading="lazy"
              alt=""
              className="modal-image"
              style={{ maxWidth: '80px' }}
            />
          </div>

          <h3 style={{ color: '#22d68a', marginBottom: '0.75rem', fontSize: '1.25rem' }}>
            ✅ Pague com PIX
          </h3>

          <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.875rem' }}>
            Escaneie o QR Code ou copie o código abaixo
          </p>

          {pixData.qr_code_url && currentQrUrl && (
            <div style={{
              margin: '0.5rem auto',
              minHeight: '180px',
              maxWidth: '220px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {qrCodeLoading && (
                <div style={{
                  padding: '1.5rem',
                  textAlign: 'center',
                  color: '#666',
                }}>
                  <img src={loadingGif} alt="Carregando..." style={{ width: '30px' }} />
                  <p style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                    Carregando QR Code...
                  </p>
                </div>
              )}

              {/* Tag <img> nativa lida com redirect 302 sem problemas de CORS */}
              <img
                src={currentQrUrl}
                alt="QR Code PIX"
                onLoad={handleQrCodeLoad}
                onError={handleQrCodeError}
                style={{
                  display: qrCodeLoading || qrCodeError ? 'none' : 'block',
                  width: '100%',
                  maxWidth: '200px',
                  border: '2px solid #22d68a',
                  borderRadius: '8px',
                  padding: '8px',
                  background: 'white',
                }}
              />

              {!qrCodeLoading && qrCodeError && (
                <div style={{
                  padding: '0.75rem',
                  background: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  color: '#856404',
                }}>
                  <p style={{ marginBottom: '0.25rem', fontSize: '0.8rem' }}>
                    ⚠️ QR Code indisponível
                  </p>
                  <p style={{ fontSize: '0.7rem', margin: 0 }}>
                    Use o código PIX abaixo
                  </p>
                </div>
              )}
            </div>
          )}

          {pixData.qr_code && (
            <div style={{ marginTop: '1rem' }}>
              <p style={{
                fontWeight: 'bold',
                marginBottom: '0.5rem',
                color: '#333',
                fontSize: '0.875rem'
              }}>
                Ou copie o código PIX:
              </p>
              <textarea
                readOnly
                value={pixData.qr_code}
                onClick={(e) => {
                  e.target.select();
                  // Tenta copiar quando clica no textarea também
                  try {
                    document.execCommand('copy');
                  } catch (err) {
                    console.log('Auto-copy ao clicar falhou, use o botão');
                  }
                }}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  maxHeight: '120px',
                  padding: '8px',
                  fontFamily: 'monospace',
                  fontSize: '0.65rem',
                  border: '2px solid #22d68a',
                  borderRadius: '4px',
                  resize: 'none',
                  marginBottom: '0.5rem',
                  cursor: 'pointer',
                  background: '#f8f8f8',
                  lineHeight: '1.3',
                  wordBreak: 'break-all',
                }}
                title="Clique para selecionar todo o código"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  copyToClipboard(pixData.qr_code);
                }}
                className="btn btn-primary btn-block cardPayment"
                style={{
                  width: '100%',
                  background: '#22d68a',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.background = '#1bb978'}
                onMouseLeave={(e) => e.target.style.background = '#22d68a'}
              >
                📋 Copiar Código PIX
              </button>

              {/* Feedback do copy - aparecer logo após o botão */}
              {error && status && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '8px 12px',
                  background: status === 'success' ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${status === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                  borderRadius: '4px',
                  color: status === 'success' ? '#155724' : '#721c24',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  textAlign: 'center',
                }}>
                  {error}
                </div>
              )}
            </div>
          )}

          {pixData.expires_at && (
            <p style={{
              marginTop: '1rem',
              fontSize: '0.75rem',
              color: '#999',
            }}>
              ⏰ Válido até: {new Date(pixData.expires_at).toLocaleString('pt-BR')}
            </p>
          )}

          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            fontSize: '0.75rem',
          }}>
            <strong style={{ fontSize: '0.8rem' }}>📱 Como pagar:</strong>
            <ol style={{
              textAlign: 'left',
              marginTop: '0.5rem',
              marginBottom: 0,
              paddingLeft: '1.25rem',
              lineHeight: '1.6'
            }}>
              <li>Abra o app do seu banco</li>
              <li>Escolha pagar com PIX</li>
              <li>Escaneie o QR Code ou cole o código</li>
              <li>Confirme o pagamento</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Formulário para coletar dados do cliente
  return (
    <div className="data-creditCard">
      <img
        className="btn-prev"
        src={arrowBack}
        alt="arrow back"
        onClick={() => goToStep(1)}
        style={{ outline: "none" }}
      />
      <div className="flex-center margin-bottom margin-medium">
        <img src={payCred} loading="lazy" alt="" style={{ maxWidth: '120px', marginBottom: '20px' }} />
      </div>

      {error && <Message text={error} status={status || "error"} />}

      <div className="form-wrapper-scroll">
        <form onSubmit={handlePixPayment} className="card-payment-scroll">
          <div className="input-group padding-input-group">
            <label className="label-input-group">Nome completo</label>
            <input
              type="text"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome e sobrenome"
              className="text-field w-input"
            />
          </div>

          <div className="input-group padding-input-group">
            <label className="label-input-group">CPF (ou CNPJ)</label>
            <TextMaskCardCPF
              name="documentNumber"
              type="text"
              className="text-field w-input"
              style={{
                color: cpfNumNotValid ? "red" : "#212",
              }}
              placeholder="Número do documento"
              value={documentNumber}
              onChange={(e) => {
                setDocumentNumber(e.target.value);
                const cleanDoc = e.target.value.replace(/\D/g, '');
                const isValid = cpfValidator.validCpf(cleanDoc) || cpfValidator.validCnpj(cleanDoc);
                setCpfNumNotValid(!isValid && cleanDoc.length > 0);
              }}
            />
          </div>

          <div className="input-group padding-input-group">
            <label className="label-input-group">Telefone (com DDD)</label>
            <input
              type="text"
              name="phone"
              value={phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                // Aplica máscara: (99) 99999-9999
                let masked = value;
                if (value.length > 0) {
                  masked = `(${value.substring(0, 2)}`;
                  if (value.length > 2) {
                    masked += `) ${value.substring(2, 7)}`;
                  }
                  if (value.length > 7) {
                    masked += `-${value.substring(7, 11)}`;
                  }
                }
                setPhone(masked);
              }}
              placeholder="(00) 00000-0000"
              className="text-field w-input"
              maxLength={15}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: "8px",
            }}
          >
            <div className="input-group padding-input-group">
              <label className="label-input-group">CEP</label>
              <TextMaskCEP
                name="postal_code"
                className="text-field w-input"
                type="text"
                placeholder="00000-000"
                value={postalCode}
                onChange={(e) => handleCepChange(e.target.value)}
                style={{
                  color: cepNotValid ? "red" : "#212",
                }}
              />
            </div>

            <div className="input-group padding-input-group">
              <label className="label-input-group">Número</label>
              <input
                type="text"
                name="streetNumber"
                value={streetNumber}
                onChange={(e) => setStreetNumber(e.target.value)}
                placeholder="N° da rua"
                className="text-field w-input"
              />
            </div>
          </div>

          <div className="input-group padding-input-group">
            <label className="label-input-group">Logradouro</label>
            <input
              type="text"
              name="street"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              placeholder="Nome da rua"
              className="text-field w-input"
            />
          </div>

          <div className="input-group padding-input-group">
            <label className="label-input-group">Bairro</label>
            <input
              type="text"
              name="neighborhood"
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
              placeholder="Bairro"
              className="text-field w-input"
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: "8px",
            }}
          >
            <div className="input-group padding-input-group">
              <label className="label-input-group">Cidade</label>
              <input
                type="text"
                name="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="text-field w-input"
                placeholder="Cidade"
              />
            </div>

            <div className="input-group padding-input-group">
              <label className="label-input-group">Estado</label>
              <select
                name="state"
                onChange={(e) => setState(e.target.value)}
                value={state}
                className="parcel-select"
              >
                <option value="" disabled></option>
                {states.map((stateOption, index) => (
                  <option key={index} value={stateOption.value}>
                    {stateOption.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary btn-block cardPayment"
            style={{
              width: '100%',
              marginTop: '1rem',
              background: loading ? '#ccc' : '#22d68a',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img style={{ width: "20px" }} src={loadingGif} alt="Carregando..." />
                <span style={{ marginLeft: '10px' }}>Gerando código PIX...</span>
              </div>
            ) : (
              <span>
                {form.discountPercentage === 100
                  ? "Finalizar Assinatura Gratuita"
                  : `Pagar ${formatter.format(calculateTotalPrice())}`}
              </span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.firebase.auth,
});

export default connect(mapStateToProps, { setUser })(PixPaymentButton);
